import React, { useEffect, useState, useMemo } from 'react';
import azureDevOpsService from './services/azureDevOpsService';
import ExpandableChartCard from './ExpandableChartCard';

const PLANNED_RELEASE = '26.1.0';
const BRANCH_FILTER = '26.1';
const TARGET_REPOS = ['argonos', 'argonos-front', 'shared-devops'];
const RELEASE_START_DATE = '2025-12-22T00:00:00Z';
const DEVELOP_END_DATE = '2026-04-13T23:59:59Z';
const BRANCH_261_START_DATE = '2026-04-14T00:00:00Z';
const BRANCH_261_END_DATE = '2026-05-07T14:59:59Z';

const TYPE_COLORS = {
  'Product Backlog Item': '#2563eb',
  'Bug': '#dc2626',
  'Demande Client': '#ca8a04',
  'Dette technique': '#7c3aed',
};

function formatAreaPath(areaPath) {
  const value = String(areaPath || 'Non renseigné').trim();
  const prefixes = [
    'Fabrique\\Tribus\\Tribu Argonos\\Squads\\',
    'Fabrique\\Tribus\\Tribu Argonos\\',
    'Fabrique\\'
  ];
  const prefix = prefixes.find(p => value.startsWith(p));
  if (!prefix) return value;
  return value.slice(prefix.length).split('\\').filter(Boolean)[0] || value;
}

const PR_STATUS_LABELS = {
  active: { label: 'Active', bg: '#dcfce7', color: '#166534' },
  completed: { label: 'Complétée', bg: '#dbeafe', color: '#1e40af' },
};

function InfoIcon({ title }) {
  return (
    <span
      className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold cursor-help"
      title={title}
      aria-label={title}
    >
      i
    </span>
  );
}

const COL_DEFS = [
  { key: 'id', label: 'ID' },
  { key: 'workItemType', label: 'Type' },
  { key: 'title', label: 'Titre' },
  { key: 'state', label: 'Statut' },
  { key: 'areaPath', label: 'Squad' },
  { key: 'plannedRelease', label: 'Planned release' },
  { key: 'repoName', label: 'Repo' },
  { key: 'prStatus', label: 'Statut PR' },
  { key: 'prId', label: 'PR' },
];

export default function KpiUntaggedPR261() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [completedReleaseItems, setCompletedReleaseItems] = useState([]);
  const [repos, setRepos] = useState([]);
  const [completedRepos, setCompletedRepos] = useState([]);
  const [apiErrors, setApiErrors] = useState([]);
  const [completedApiErrors, setCompletedApiErrors] = useState([]);
  const [search, setSearch] = useState('');
  const [repoFilter, setRepoFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [sortKey, setSortKey] = useState('id');
  const [sortDir, setSortDir] = useState('asc');
  const [prStatusFilter, setPrStatusFilter] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedCompletedIds, setSelectedCompletedIds] = useState([]);
  const [patchingIds, setPatchingIds] = useState([]);
  const [batchPatching, setBatchPatching] = useState(false);
  const [batchPatchingCompleted, setBatchPatchingCompleted] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      azureDevOpsService.getUntaggedWorkItemsFromPRsOn261Branch(
        TARGET_REPOS,
        BRANCH_FILTER,
        PLANNED_RELEASE
      ),
      azureDevOpsService.getUntaggedWorkItemsFromPRsOn261Branch(
        TARGET_REPOS,
        BRANCH_FILTER,
        PLANNED_RELEASE,
        {
          prStatuses: ['completed'],
          releaseFieldNames: ['Custom.Release'],
          defaultQueryTimeRangeType: 'closed',
          prScopes: [
            {
              branchFilter: 'develop',
              targetRefName: 'refs/heads/develop',
              fromDate: RELEASE_START_DATE,
              toDate: DEVELOP_END_DATE,
              queryTimeRangeType: 'closed'
            },
            {
              branchFilter: BRANCH_FILTER,
              targetRefName: `refs/heads/${BRANCH_FILTER}`,
              fromDate: BRANCH_261_START_DATE,
              toDate: BRANCH_261_END_DATE,
              queryTimeRangeType: 'closed'
            }
          ]
        }
      )
    ])
      .then(([plannedResult, customReleaseResult]) => {
        if (cancelled) return;
        setItems(plannedResult.items || []);
        setRepos(plannedResult.repos || []);
        setApiErrors(plannedResult.errors || []);

        const customItems = (customReleaseResult.items || []).map(item => ({
          ...item,
          customRelease: item.plannedRelease || ''
        }));
        setCompletedReleaseItems(customItems);
        setCompletedRepos(customReleaseResult.repos || []);
        setCompletedApiErrors(customReleaseResult.errors || []);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message || 'Erreur de chargement');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const combinedItems = useMemo(
    () => [...items, ...completedReleaseItems],
    [items, completedReleaseItems]
  );

  const availableRepos = useMemo(() => {
    // Un ticket peut être lié à plusieurs repos — on déduplique via repoNames (tableau)
    const set = new Set(combinedItems.flatMap(i => i.repoNames || []));
    return TARGET_REPOS.filter(r => set.has(r));
  }, [combinedItems]);

  const availableTypes = useMemo(() => {
    const set = new Set(combinedItems.map(i => i.workItemType).filter(Boolean));
    return Array.from(set).sort();
  }, [combinedItems]);

  const filtered = useMemo(() => {
    let result = items;

    if (repoFilter.length > 0) {
      // Garder les tickets liés à au moins un des repos sélectionnés
      result = result.filter(i => (i.repoNames || []).some(r => repoFilter.includes(r)));
    }
    if (typeFilter.length > 0) {
      result = result.filter(i => typeFilter.includes(i.workItemType));
    }
    if (prStatusFilter.length > 0) {
      result = result.filter(i =>
        (i.prList || []).some(pr => prStatusFilter.includes(pr.prStatus))
      );
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(i =>
        String(i.id).includes(q) ||
        (i.title || '').toLowerCase().includes(q) ||
        (i.state || '').toLowerCase().includes(q) ||
        (i.areaPath || '').toLowerCase().includes(q) ||
        (i.prTitle || '').toLowerCase().includes(q) ||
        (i.sourceTickets || []).some(source =>
          String(source.id).includes(q) ||
          (source.title || '').toLowerCase().includes(q) ||
          (source.type || '').toLowerCase().includes(q)
        )
      );
    }

    return [...result].sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (av === null || av === undefined) av = '';
      if (bv === null || bv === undefined) bv = '';
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv), undefined, { sensitivity: 'base', numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [items, repoFilter, typeFilter, prStatusFilter, search, sortKey, sortDir]);

  const filteredIds = useMemo(() => filtered.map(item => item.id), [filtered]);
  const completedOnlyFiltered = useMemo(() => {
    let result = completedReleaseItems;

    if (repoFilter.length > 0) {
      result = result.filter(i => (i.repoNames || []).some(r => repoFilter.includes(r)));
    }
    if (typeFilter.length > 0) {
      result = result.filter(i => typeFilter.includes(i.workItemType));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(i =>
        String(i.id).includes(q) ||
        (i.title || '').toLowerCase().includes(q) ||
        (i.state || '').toLowerCase().includes(q) ||
        (i.areaPath || '').toLowerCase().includes(q) ||
        (i.prTitle || '').toLowerCase().includes(q) ||
        (i.sourceTickets || []).some(source =>
          String(source.id).includes(q) ||
          (source.title || '').toLowerCase().includes(q) ||
          (source.type || '').toLowerCase().includes(q)
        )
      );
    }

    return [...result].sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (av === null || av === undefined) av = '';
      if (bv === null || bv === undefined) bv = '';
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv), undefined, { sensitivity: 'base', numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [completedReleaseItems, repoFilter, typeFilter, search, sortKey, sortDir]);
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every(id => selectedIds.includes(id));
  const completedFilteredIds = useMemo(
    () => completedOnlyFiltered.map(item => item.id),
    [completedOnlyFiltered]
  );
  const allCompletedFilteredSelected =
    completedFilteredIds.length > 0 && completedFilteredIds.every(id => selectedCompletedIds.includes(id));

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  function SortIndicator({ colKey }) {
    if (sortKey !== colKey) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  function toggleFilter(setter, value) {
    setter(prev => prev.includes(value) ? prev.filter(x => x !== value) : [...prev, value]);
  }

  function renderFiltersBar({
    showPrStatus = true,
    selectedCount = 0,
    onBatchPatch,
    batchBusy = false,
    batchLabel = `Ajouter ${PLANNED_RELEASE} (batch)`
  }) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-3">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher (ID, titre, statut…)"
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-60 focus:outline-none focus:ring-1 focus:ring-blue-400"
        />

        {availableRepos.length > 1 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Repo :</span>
            {availableRepos.map(repo => (
              <button
                key={repo}
                onClick={() => toggleFilter(setRepoFilter, repo)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                  repoFilter.length === 0 || repoFilter.includes(repo)
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-400 border-gray-200'
                }`}
              >
                {repo}
              </button>
            ))}
            {repoFilter.length > 0 && (
              <button onClick={() => setRepoFilter([])} className="px-2.5 py-1 rounded-full text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors">✕</button>
            )}
          </div>
        )}

        {showPrStatus && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Statut PR :</span>
            {Object.entries(PR_STATUS_LABELS).map(([status, { label, bg, color }]) => {
              const isActive = prStatusFilter.length === 0 || prStatusFilter.includes(status);
              return (
                <button
                  key={status}
                  onClick={() => toggleFilter(setPrStatusFilter, status)}
                  className="px-2.5 py-1 rounded-full text-xs font-medium border transition-colors"
                  style={isActive ? { backgroundColor: bg, color, borderColor: color } : { backgroundColor: 'white', color: '#9ca3af', borderColor: '#e5e7eb' }}
                >
                  {label}
                </button>
              );
            })}
            {prStatusFilter.length > 0 && (
              <button onClick={() => setPrStatusFilter([])} className="px-2.5 py-1 rounded-full text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors">✕</button>
            )}
          </div>
        )}

        {availableTypes.length > 1 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Type :</span>
            {availableTypes.map(type => {
              const color = TYPE_COLORS[type] || '#6b7280';
              const isActive = typeFilter.length === 0 || typeFilter.includes(type);
              return (
                <button
                  key={type}
                  onClick={() => toggleFilter(setTypeFilter, type)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    isActive ? 'text-white border-transparent' : 'bg-white text-gray-400 border-gray-200'
                  }`}
                  style={isActive ? { backgroundColor: color, borderColor: color } : {}}
                >
                  {type}
                </button>
              );
            })}
            {typeFilter.length > 0 && (
              <button onClick={() => setTypeFilter([])} className="px-2.5 py-1 rounded-full text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors">✕</button>
            )}
          </div>
        )}

        {(repoFilter.length > 0 || typeFilter.length > 0 || prStatusFilter.length > 0 || search) && (
          <button
            onClick={() => { setRepoFilter([]); setTypeFilter([]); setPrStatusFilter([]); setSearch(''); }}
            className="px-3 py-1 rounded-full text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Tout effacer
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500">{selectedCount} sélectionné(s)</span>
          <button
            onClick={onBatchPatch}
            disabled={selectedCount === 0 || batchBusy}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
              selectedCount === 0 || batchBusy
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
            }`}
          >
            {batchBusy ? 'Patch en cours...' : batchLabel}
          </button>
        </div>
      </div>
    );
  }

  function toggleRowSelection(id) {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(value => value !== id) : [...prev, id]
    );
  }

  function toggleSelectAllFiltered() {
    if (allFilteredSelected) {
      setSelectedIds(prev => prev.filter(id => !filteredIds.includes(id)));
      return;
    }

    setSelectedIds(prev => {
      const next = new Set(prev);
      filteredIds.forEach(id => next.add(id));
      return Array.from(next);
    });
  }

  function toggleCompletedRowSelection(id) {
    setSelectedCompletedIds(prev =>
      prev.includes(id) ? prev.filter(value => value !== id) : [...prev, id]
    );
  }

  function toggleSelectAllCompletedFiltered() {
    if (allCompletedFilteredSelected) {
      setSelectedCompletedIds(prev => prev.filter(id => !completedFilteredIds.includes(id)));
      return;
    }

    setSelectedCompletedIds(prev => {
      const next = new Set(prev);
      completedFilteredIds.forEach(id => next.add(id));
      return Array.from(next);
    });
  }

  useEffect(() => {
    // Nettoyer la sélection quand des tickets disparaissent après patch.
    setSelectedIds(prev => prev.filter(id => items.some(item => item.id === id)));
    setSelectedCompletedIds(prev => prev.filter(id => completedReleaseItems.some(item => item.id === id)));
  }, [items, completedReleaseItems]);

  async function patchWorkItems(ids) {
    if (!ids || ids.length === 0) return;

    const currentPlannedReleaseById = {};
    items.forEach(item => {
      if (ids.includes(item.id)) {
        currentPlannedReleaseById[item.id] = item.plannedRelease || '';
      }
    });

    const result = await azureDevOpsService.addPlannedReleaseToWorkItems(ids, PLANNED_RELEASE, {
      currentPlannedReleaseById
    });

    const processedIds = [...result.updatedIds, ...result.skippedIds];
    if (processedIds.length > 0) {
      setItems(prev => prev.filter(item => !processedIds.includes(item.id)));
      setSelectedIds(prev => prev.filter(id => !processedIds.includes(id)));
    }

    const messageParts = [];
    if (result.updatedCount > 0) messageParts.push(`${result.updatedCount} ticket(s) patché(s)`);
    if (result.skippedCount > 0) messageParts.push(`${result.skippedCount} déjà tagué(s)`);
    if (result.failedCount > 0) messageParts.push(`${result.failedCount} en erreur`);

    setActionMessage(messageParts.join(' · '));
    if (result.failedCount > 0) {
      const firstError = result.failed[0]?.error || 'Erreur inconnue';
      setActionError(`Patch partiel: ${firstError}`);
    } else {
      setActionError('');
    }
  }

  async function patchCustomReleaseWorkItems(ids) {
    if (!ids || ids.length === 0) return;

    const currentReleaseById = {};
    completedReleaseItems.forEach(item => {
      if (ids.includes(item.id)) {
        currentReleaseById[item.id] = item.customRelease || '';
      }
    });

    const result = await azureDevOpsService.addPlannedReleaseToWorkItems(ids, PLANNED_RELEASE, {
      plannedReleaseFieldName: 'Custom.Release',
      currentPlannedReleaseById: currentReleaseById
    });

    const processedIds = [...result.updatedIds, ...result.skippedIds];
    if (processedIds.length > 0) {
      setCompletedReleaseItems(prev => prev.filter(item => !processedIds.includes(item.id)));
      setSelectedCompletedIds(prev => prev.filter(id => !processedIds.includes(id)));
    }

    const messageParts = [];
    if (result.updatedCount > 0) messageParts.push(`${result.updatedCount} ticket(s) patché(s) (Custom.Release)`);
    if (result.skippedCount > 0) messageParts.push(`${result.skippedCount} déjà tagué(s)`);
    if (result.failedCount > 0) messageParts.push(`${result.failedCount} en erreur`);

    setActionMessage(messageParts.join(' · '));
    if (result.failedCount > 0) {
      const firstError = result.failed[0]?.error || 'Erreur inconnue';
      setActionError(`Patch partiel: ${firstError}`);
    } else {
      setActionError('');
    }
  }

  async function patchSingle(item) {
    const id = item.id;
    setActionMessage('');
    setActionError('');
    setPatchingIds(prev => [...prev, id]);

    try {
      await patchWorkItems([id]);
    } catch (e) {
      setActionError(e.message || `Erreur patch work item ${id}`);
    } finally {
      setPatchingIds(prev => prev.filter(value => value !== id));
    }
  }

  async function patchBatch() {
    if (selectedIds.length === 0) return;

    setActionMessage('');
    setActionError('');
    setBatchPatching(true);

    try {
      await patchWorkItems(selectedIds);
    } catch (e) {
      setActionError(e.message || 'Erreur patch batch');
    } finally {
      setBatchPatching(false);
    }
  }

  async function patchSingleCustomRelease(item) {
    const id = item.id;
    setActionMessage('');
    setActionError('');
    setPatchingIds(prev => [...prev, id]);

    try {
      await patchCustomReleaseWorkItems([id]);
    } catch (e) {
      setActionError(e.message || `Erreur patch Custom.Release du work item ${id}`);
    } finally {
      setPatchingIds(prev => prev.filter(value => value !== id));
    }
  }

  async function patchBatchCustomRelease() {
    if (selectedCompletedIds.length === 0) return;

    setActionMessage('');
    setActionError('');
    setBatchPatchingCompleted(true);

    try {
      await patchCustomReleaseWorkItems(selectedCompletedIds);
    } catch (e) {
      setActionError(e.message || 'Erreur patch batch Custom.Release');
    } finally {
      setBatchPatchingCompleted(false);
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-2">Tickets non tagués 26.1.0 liés aux PRs branche 26.1</h2>
        <div className="text-gray-500">Chargement en cours — récupération des PRs sur {TARGET_REPOS.join(', ')}...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-2">Tickets non tagués 26.1.0 liés aux PRs branche 26.1</h2>
        <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded">Erreur : {error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-semibold text-gray-900">Tickets non tagués 26.1.0</h2>
        <p className="text-gray-600 mt-1">
          Tickets liés à des PRs sur des branches <strong>{BRANCH_FILTER}</strong> dans{' '}
          <strong>{TARGET_REPOS.join(', ')}</strong> mais sans planned release <strong>{PLANNED_RELEASE}</strong>.
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Tableau 1: contrôle sur le champ <strong>Planned release</strong>. Tableau 2: contrôle sur le champ <strong>Custom.Release</strong> (PR complétées vers <strong>develop</strong> du 22/12 au 13/04 puis vers <strong>{BRANCH_FILTER}</strong> depuis le 14/04).
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Règle Task/Port : pour les tickets de type <strong>Task</strong> ou <strong>Port</strong>, la vérification du planned release est faite sur les tickets liés (relation <em>Parent</em> ou <em>Related</em>) ; si l&apos;un d&apos;eux contient déjà <strong>{PLANNED_RELEASE}</strong>, la ligne n&apos;est pas affichée.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 px-2.5 py-1 border border-blue-200">
            Tableau 1 (Planned release): {filtered.length}
            {filtered.length !== items.length ? ` / ${items.length}` : ''}
          </span>
          <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 border border-emerald-200">
            Tableau 2 (Custom.Release): {completedOnlyFiltered.length}
            {completedOnlyFiltered.length !== completedReleaseItems.length ? ` / ${completedReleaseItems.length}` : ''}
          </span>
        </div>
        {repos.length > 0 && (
          <p className="text-xs text-gray-400 mt-1">
            Repos interrogés : {repos.map(r => r.name).join(', ')}
          </p>
        )}
        {apiErrors.length > 0 && (
          <div className="mt-3 bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded text-sm">
            <strong>Avertissements :</strong>
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              {apiErrors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}
        {completedApiErrors.length > 0 && (
          <div className="mt-3 bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded text-sm">
            <strong>Avertissements tableau PR complétées / Custom.Release :</strong>
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              {completedApiErrors.map((e, i) => <li key={`completed-error-${i}`}>{e}</li>)}
            </ul>
          </div>
        )}
        {(completedRepos || []).length > 0 && (
          <p className="text-xs text-gray-400 mt-1">
            Repos interrogés (tableau PR complétées / Custom.Release) : {completedRepos.map(r => r.name).join(', ')}
          </p>
        )}
      </div>

      {/* KPI summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-5">
          <div className="text-sm uppercase tracking-wide text-gray-500">Total</div>
          <div className="text-3xl font-semibold text-gray-900 mt-2">{filtered.length}</div>
          {filtered.length !== items.length && (
            <div className="text-xs text-gray-400 mt-1">{items.length} au total</div>
          )}
        </div>
        {TARGET_REPOS.map(repo => {
          const count = filtered.filter(i => (i.repoNames || []).includes(repo)).length;
          return (
            <div key={repo} className="bg-white rounded-lg shadow p-5">
              <div className="text-sm uppercase tracking-wide text-gray-500">{repo}</div>
              <div className="text-3xl font-semibold text-gray-900 mt-2">{count}</div>
            </div>
          );
        })}
      </div>


      {(actionMessage || actionError) && (
        <div className="bg-white rounded-lg shadow px-5 py-3">
          {actionMessage && <div className="text-sm text-emerald-700">{actionMessage}</div>}
          {actionError && <div className="text-sm text-red-700 mt-1">{actionError}</div>}
        </div>
      )}

      {/* Table */}
      <ExpandableChartCard
        title={`Tickets non tagués 26.1.0 liés aux PRs 26.1 (${filtered.length})`}
        subtitle="Champ contrôlé: Planned release (Custom.PlannedRelease / variantes). Utilisez le bouton d’agrandissement en haut à droite pour afficher le tableau en plein écran."
        headerExtra={renderFiltersBar({
          showPrStatus: true,
          selectedCount: selectedIds.length,
          onBatchPatch: patchBatch,
          batchBusy: batchPatching,
          batchLabel: `Ajouter ${PLANNED_RELEASE} (batch)`
        })}
        normalHeight={460}
        fullscreenHeight={760}
      >
        {(height) => filtered.length === 0 ? (
          <div className="px-6 py-10 text-center text-gray-400">
            {items.length === 0
              ? 'Aucun ticket non tagué trouvé — tous les tickets liés aux PRs 26.1 ont bien le planned release 26.1.0 ✓'
              : 'Aucun résultat pour les filtres sélectionnés.'}
          </div>
        ) : (
          <div className="overflow-auto" style={{ maxHeight: `${height}px` }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleSelectAllFiltered}
                      aria-label="Selectionner toutes les lignes visibles"
                    />
                  </th>
                  {COL_DEFS.map(col => (
                    <th
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-800 select-none whitespace-nowrap"
                    >
                      {col.label}<SortIndicator colKey={col.key} />
                    </th>
                  ))}
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((item, i) => {
                  const typeColor = TYPE_COLORS[item.workItemType] || '#6b7280';
                  const hasSourceTickets = (item.sourceTickets || []).length > 0;
                  const sourceInfoTitle = hasSourceTickets
                    ? item.sourceTickets.map(source => {
                        const relationLabel = source.relationType === 'parent'
                          ? 'parent'
                          : 'ticket lié via la relation Related';
                        return `La PR est liée au ticket source ${source.type || 'Work Item'} #${source.id}${source.title ? ` - ${source.title}` : ''}. Cette ligne affiche son ${relationLabel}: ${item.workItemType || 'Work Item'} #${item.id}${item.title ? ` - ${item.title}` : ''}.`;
                      }).join(' | ')
                    : '';

                  return (
                    <tr key={item.id} className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(item.id)}
                          onChange={() => toggleRowSelection(item.id)}
                          aria-label={`Selectionner le ticket ${item.id}`}
                        />
                      </td>
                      {/* ID */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline font-mono font-medium"
                          >#{item.id}</a>
                          {hasSourceTickets && <InfoIcon title={sourceInfoTitle} />}
                        </div>
                      </td>
                      {/* Type */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span
                          className="text-xs font-medium px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: typeColor + '18', color: typeColor, border: `1px solid ${typeColor}40` }}
                        >{item.workItemType || '—'}</span>
                      </td>
                      {/* Titre */}
                      <td className="px-3 py-3 max-w-xs">
                        <span className="text-gray-800 leading-snug line-clamp-2" title={item.title}>{item.title}</span>
                      </td>
                      {/* Statut */}
                      <td className="px-3 py-3 whitespace-nowrap text-gray-600 text-xs">{item.state || '—'}</td>
                      {/* Squad */}
                      <td className="px-3 py-3 whitespace-nowrap text-gray-600 text-xs">{formatAreaPath(item.areaPath)}</td>
                      {/* Planned release */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {item.plannedRelease ? (
                          <span className="text-xs text-gray-600 font-mono">{item.plannedRelease}</span>
                        ) : (
                          <span className="text-xs text-red-500 font-medium">Non défini</span>
                        )}
                      </td>
                      {/* Repo */}
                      <td className="px-3 py-3 text-xs text-gray-600 font-mono">
                        {(item.repoNames || []).length > 0
                          ? item.repoNames.join(', ')
                          : '—'}
                      </td>
                      {/* Statut PR — première PR */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {item.prList && item.prList.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {[...new Set(item.prList.map(p => p.prStatus))].map(s => {
                              const cfg = PR_STATUS_LABELS[s] || { label: s, bg: '#f3f4f6', color: '#374151' };
                              return (
                                <span key={s} className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                                  {cfg.label}
                                </span>
                              );
                            })}
                          </div>
                        ) : '—'}
                      </td>
                      {/* PR link(s) */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {item.prList && item.prList.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {item.prList.map(p => (
                              <a
                                key={`${p.repoName}-${p.prId}`}
                                href={p.prUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 hover:underline font-mono font-medium text-xs"
                                title={`${p.repoName} — ${p.prTitle}`}
                              >#{p.prId}</a>
                            ))}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <button
                          onClick={() => patchSingle(item)}
                          disabled={patchingIds.includes(item.id) || batchPatching}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                            patchingIds.includes(item.id) || batchPatching
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                          }`}
                        >
                          {patchingIds.includes(item.id) ? 'Patch...' : 'Patcher'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </ExpandableChartCard>

      {/* Table 2: completed PRs only */}
      <ExpandableChartCard
        title={`Tickets non tagués 26.1.0 avec PRs complétées (${completedOnlyFiltered.length})`}
        subtitle="Champ contrôlé: Custom.Release. Périmètre PRs: complétées vers develop (22/12 -> 13/04) puis vers 26.1 (depuis 14/04)."
        headerExtra={renderFiltersBar({
          showPrStatus: false,
          selectedCount: selectedCompletedIds.length,
          onBatchPatch: patchBatchCustomRelease,
          batchBusy: batchPatchingCompleted,
          batchLabel: `Ajouter ${PLANNED_RELEASE} dans Custom.Release (batch)`
        })}
        normalHeight={420}
        fullscreenHeight={720}
      >
        {(height) => completedOnlyFiltered.length === 0 ? (
          <div className="px-6 py-10 text-center text-gray-400">
            Aucun ticket non tagué lié à une PR 26.1 complétée pour les filtres sélectionnés.
          </div>
        ) : (
          <div className="overflow-auto" style={{ maxHeight: `${height}px` }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={allCompletedFilteredSelected}
                      onChange={toggleSelectAllCompletedFiltered}
                      aria-label="Selectionner toutes les lignes visibles du tableau PR complétées"
                    />
                  </th>
                  {COL_DEFS.map(col => (
                    <th
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-800 select-none whitespace-nowrap"
                    >
                      {col.key === 'plannedRelease' ? 'Custom.Release' : col.label}<SortIndicator colKey={col.key} />
                    </th>
                  ))}
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {completedOnlyFiltered.map((item, i) => {
                  const typeColor = TYPE_COLORS[item.workItemType] || '#6b7280';
                  const hasSourceTickets = (item.sourceTickets || []).length > 0;
                  const completedPrs = (item.prList || []).filter(pr => pr.prStatus === 'completed');
                  const sourceInfoTitle = hasSourceTickets
                    ? item.sourceTickets.map(source => {
                        const relationLabel = source.relationType === 'parent'
                          ? 'parent'
                          : 'ticket lié via la relation Related';
                        return `La PR est liée au ticket source ${source.type || 'Work Item'} #${source.id}${source.title ? ` - ${source.title}` : ''}. Cette ligne affiche son ${relationLabel}: ${item.workItemType || 'Work Item'} #${item.id}${item.title ? ` - ${item.title}` : ''}.`;
                      }).join(' | ')
                    : '';

                  return (
                    <tr key={`completed-${item.id}`} className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedCompletedIds.includes(item.id)}
                          onChange={() => toggleCompletedRowSelection(item.id)}
                          aria-label={`Selectionner le ticket ${item.id} (tableau PR complétées)`}
                        />
                      </td>
                      {/* ID */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline font-mono font-medium"
                          >#{item.id}</a>
                          {hasSourceTickets && <InfoIcon title={sourceInfoTitle} />}
                        </div>
                      </td>
                      {/* Type */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span
                          className="text-xs font-medium px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: typeColor + '18', color: typeColor, border: `1px solid ${typeColor}40` }}
                        >{item.workItemType || '—'}</span>
                      </td>
                      {/* Titre */}
                      <td className="px-3 py-3 max-w-xs">
                        <span className="text-gray-800 leading-snug line-clamp-2" title={item.title}>{item.title}</span>
                      </td>
                      {/* Statut */}
                      <td className="px-3 py-3 whitespace-nowrap text-gray-600 text-xs">{item.state || '—'}</td>
                      {/* Squad */}
                      <td className="px-3 py-3 whitespace-nowrap text-gray-600 text-xs">{formatAreaPath(item.areaPath)}</td>
                      {/* Custom.Release */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {item.customRelease ? (
                          <span className="text-xs text-gray-600 font-mono">{item.customRelease}</span>
                        ) : (
                          <span className="text-xs text-red-500 font-medium">Non défini</span>
                        )}
                      </td>
                      {/* Repo */}
                      <td className="px-3 py-3 text-xs text-gray-600 font-mono">
                        {(item.repoNames || []).length > 0
                          ? item.repoNames.join(', ')
                          : '—'}
                      </td>
                      {/* Statut PR */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: PR_STATUS_LABELS.completed.bg, color: PR_STATUS_LABELS.completed.color }}>
                          {PR_STATUS_LABELS.completed.label}
                        </span>
                      </td>
                      {/* PR links: completed only */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {completedPrs.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {completedPrs.map(p => (
                              <a
                                key={`${p.repoName}-${p.prId}-completed`}
                                href={p.prUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 hover:underline font-mono font-medium text-xs"
                                title={`${p.repoName} — ${p.prTitle}`}
                              >#{p.prId}</a>
                            ))}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <button
                          onClick={() => patchSingleCustomRelease(item)}
                          disabled={patchingIds.includes(item.id) || batchPatching || batchPatchingCompleted}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                            patchingIds.includes(item.id) || batchPatching || batchPatchingCompleted
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                          }`}
                        >
                          {patchingIds.includes(item.id) ? 'Patch...' : 'Patcher'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </ExpandableChartCard>
    </div>
  );
}
